import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { ClerkBridge, clerkPublishableKey } from "@lib/auth";
import { SiteShell } from "@components/SiteShell";
import { ProtectedRoute } from "@components/ProtectedRoute";
import "@styles/globals.css";

const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

function Loader() {
  return (
    <div className="mx-auto flex min-h-[55vh] max-w-7xl items-center justify-center px-4">
      <div className="rounded-brand border border-brand-line bg-white px-6 py-4 font-bold text-brand-text/65 shadow-soft">
        Loading Aladdin Shenewa...
      </div>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <SiteShell />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/services", element: <Services /> },
      { path: "/portfolio", element: <Portfolio /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/about", element: <About /> },
      { path: "/testimonials", element: <Testimonials /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/contact", element: <Contact /> },
      { path: "/login/*", element: <Login /> },
      { path: "/signup/*", element: <Signup /> },
      { path: "/sso/*", element: <Login /> },
      { path: "/reset-password/*", element: <Login /> },
      { path: "/dashboard", element: (
        <ProtectedRoute>
          <ClientDashboard />
        </ProtectedRoute>
      )},
      { path: "/admin", element: (
        <ProtectedRoute role="admin">
          <AdminDashboard />
        </ProtectedRoute>
      )},
      { path: "/blog", element: <Blog /> },
      { path: "*", element: <NotFound /> }
    ]
  }
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPublishableKey || ""}
      afterSignOutUrl="/"
      signInUrl="/login"
      signUpUrl="/signup"
    >
      <QueryClientProvider client={queryClient}>
        <ClerkBridge>
          <Suspense fallback={<Loader />}>
            <RouterProvider router={router} />
          </Suspense>
        </ClerkBridge>
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>
);
